import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  confirmSignUp,
  signIn,
  getCurrentSession,
  resendVerificationCode,
  createWorkspaceAfterSignup,
  acceptInvitationAfterSignup,
} from '@/api/auth';
import { useSession } from '@/hooks/useSession';
import { useNotification } from '@/hooks/useNotification';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { loadConfirmFormData, clearConfirmFormData } from './utils';

export default function ConfirmSignupPage() {
  const { userHasAuthenticated, initiateSession, updateIsQuerying, updateLoginProcessOngoing } = useSession();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const [formData] = useState(() => loadConfirmFormData());
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!formData) {
      navigate('/signup');
    }
  }, [formData, navigate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!formData) return;

    setLoading(true);
    setError(false);
    updateIsQuerying(true);

    try {
      // Step 1: Confirm registration
      await confirmSignUp(formData.email, code);

      // Step 2: Log the user in
      await signIn(formData.email, formData.password);

      // Step 3: Get session for API calls
      const session = await getCurrentSession();

      // Step 4: Create workspace or accept invitation
      if (formData.invitationId) {
        await acceptInvitationAfterSignup(
          session,
          formData.invitationId,
          formData.firstName,
          formData.lastName,
        );
      } else {
        await createWorkspaceAfterSignup(
          session,
          formData.companyName,
          formData.userSub,
          formData.firstName,
          formData.lastName,
          formData.email,
        );
      }

      // Done
      clearConfirmFormData();
      userHasAuthenticated(true);
      initiateSession();
      updateLoginProcessOngoing(true);
    } catch {
      notify({ id: String(Date.now()), type: 'error', message: 'Could not confirm registration' });
      setError(true);
      setCode('');
    } finally {
      setLoading(false);
      updateIsQuerying(false);
    }
  }

  async function handleResendCode() {
    if (!formData) return;
    setResending(true);
    updateIsQuerying(true);
    try {
      await resendVerificationCode(formData.email);
      notify({
        id: String(Date.now()),
        type: 'success',
        message: `Code sent to ${formData.email}`,
      });
    } catch {
      notify({
        id: String(Date.now()),
        type: 'error',
        message: 'Could not send verification code',
      });
    } finally {
      setResending(false);
      updateIsQuerying(false);
    }
  }

  if (!formData) return null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border border-border/60 bg-card p-8 shadow-xl">
        <h2 className="mb-2 text-xl font-semibold">Confirm signup</h2>
        <p className="mb-6 text-sm text-muted-foreground">Email: {formData.email}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="code" className="mb-1 block text-sm font-medium text-accent-foreground">
              Confirmation code
            </label>
            <Input
              id="code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
            />
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={handleResendCode}
              disabled={resending}
              className="flex-1"
            >
              {resending && <Spinner size="sm" />}
              Resend code
            </Button>
            <Button
              type="submit"
              disabled={!code || loading}
              className="flex-1"
            >
              {loading && <Spinner size="sm" />}
              {error ? 'Try again' : 'Confirm'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
